/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  addDecoderSizePrefix,
  addEncoderSizePrefix,
  combineCodec,
  fixDecoderSize,
  fixEncoderSize,
  getBytesDecoder,
  getBytesEncoder,
  getStructDecoder,
  getStructEncoder,
  getU32Decoder,
  getU32Encoder,
  getUtf8Decoder,
  getUtf8Encoder,
  transformEncoder,
  type AccountMeta,
  type AccountSignerMeta,
  type Address,
  type Codec,
  type Decoder,
  type Encoder,
  type Instruction,
  type InstructionWithAccounts,
  type InstructionWithData,
  type ReadonlyAccount,
  type ReadonlySignerAccount,
  type ReadonlyUint8Array,
  type TransactionSigner,
  type WritableAccount,
} from "@solana/kit";
import { GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS } from "../programs";
import { getAccountMetaFactory, type ResolvedAccount } from "../shared";

export const SUBMIT_DELIVERY_DISCRIMINATOR = new Uint8Array([
  217, 177, 33, 54, 136, 185, 123, 96,
]);

export function getSubmitDeliveryDiscriminatorBytes() {
  return fixEncoderSize(getBytesEncoder(), 8).encode(
    SUBMIT_DELIVERY_DISCRIMINATOR,
  );
}

export type SubmitDeliveryInstruction<
  TProgram extends string = typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
  TAccountEscrow extends string | AccountMeta<string> = string,
  TAccountAgent extends string | AccountMeta<string> = string,
  TAccountAgentOwner extends string | AccountMeta<string> = string,
  TRemainingAccounts extends readonly AccountMeta<string>[] = [],
> = Instruction<TProgram> &
  InstructionWithData<ReadonlyUint8Array> &
  InstructionWithAccounts<
    [
      TAccountEscrow extends string
        ? WritableAccount<TAccountEscrow>
        : TAccountEscrow,
      TAccountAgent extends string
        ? ReadonlyAccount<TAccountAgent>
        : TAccountAgent,
      TAccountAgentOwner extends string
        ? ReadonlySignerAccount<TAccountAgentOwner> &
            AccountSignerMeta<TAccountAgentOwner>
        : TAccountAgentOwner,
      ...TRemainingAccounts,
    ]
  >;

export type SubmitDeliveryInstructionData = {
  discriminator: ReadonlyUint8Array;
  deliveryProof: string;
};

export type SubmitDeliveryInstructionDataArgs = { deliveryProof: string };

export function getSubmitDeliveryInstructionDataEncoder(): Encoder<SubmitDeliveryInstructionDataArgs> {
  return transformEncoder(
    getStructEncoder([
      ["discriminator", fixEncoderSize(getBytesEncoder(), 8)],
      [
        "deliveryProof",
        addEncoderSizePrefix(getUtf8Encoder(), getU32Encoder()),
      ],
    ]),
    (value) => ({ ...value, discriminator: SUBMIT_DELIVERY_DISCRIMINATOR }),
  );
}

export function getSubmitDeliveryInstructionDataDecoder(): Decoder<SubmitDeliveryInstructionData> {
  return getStructDecoder([
    ["discriminator", fixDecoderSize(getBytesDecoder(), 8)],
    ["deliveryProof", addDecoderSizePrefix(getUtf8Decoder(), getU32Decoder())],
  ]);
}

export function getSubmitDeliveryInstructionDataCodec(): Codec<
  SubmitDeliveryInstructionDataArgs,
  SubmitDeliveryInstructionData
> {
  return combineCodec(
    getSubmitDeliveryInstructionDataEncoder(),
    getSubmitDeliveryInstructionDataDecoder(),
  );
}

export type SubmitDeliveryInput<
  TAccountEscrow extends string = string,
  TAccountAgent extends string = string,
  TAccountAgentOwner extends string = string,
> = {
  escrow: Address<TAccountEscrow>;
  agent: Address<TAccountAgent>;
  agentOwner: TransactionSigner<TAccountAgentOwner>;
  deliveryProof: SubmitDeliveryInstructionDataArgs["deliveryProof"];
};

export function getSubmitDeliveryInstruction<
  TAccountEscrow extends string,
  TAccountAgent extends string,
  TAccountAgentOwner extends string,
  TProgramAddress extends Address =
    typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
>(
  input: SubmitDeliveryInput<TAccountEscrow, TAccountAgent, TAccountAgentOwner>,
  config?: { programAddress?: TProgramAddress },
): SubmitDeliveryInstruction<
  TProgramAddress,
  TAccountEscrow,
  TAccountAgent,
  TAccountAgentOwner
> {
  // Program address.
  const programAddress =
    config?.programAddress ?? GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    escrow: { value: input.escrow ?? null, isWritable: true },
    agent: { value: input.agent ?? null, isWritable: false },
    agentOwner: { value: input.agentOwner ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedAccount
  >;

  // Original args.
  const args = { ...input };

  const getAccountMeta = getAccountMetaFactory(programAddress, "programId");
  return Object.freeze({
    accounts: [
      getAccountMeta(accounts.escrow),
      getAccountMeta(accounts.agent),
      getAccountMeta(accounts.agentOwner),
    ],
    data: getSubmitDeliveryInstructionDataEncoder().encode(
      args as SubmitDeliveryInstructionDataArgs,
    ),
    programAddress,
  } as SubmitDeliveryInstruction<
    TProgramAddress,
    TAccountEscrow,
    TAccountAgent,
    TAccountAgentOwner
  >);
}

export type ParsedSubmitDeliveryInstruction<
  TProgram extends string = typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
  TAccountMetas extends readonly AccountMeta[] = readonly AccountMeta[],
> = {
  programAddress: Address<TProgram>;
  accounts: {
    escrow: TAccountMetas[0];
    agent: TAccountMetas[1];
    agentOwner: TAccountMetas[2];
  };
  data: SubmitDeliveryInstructionData;
};

export function parseSubmitDeliveryInstruction<
  TProgram extends string,
  TAccountMetas extends readonly AccountMeta[],
>(
  instruction: Instruction<TProgram> &
    InstructionWithAccounts<TAccountMetas> &
    InstructionWithData<ReadonlyUint8Array>,
): ParsedSubmitDeliveryInstruction<TProgram, TAccountMetas> {
  if (instruction.accounts.length < 3) {
    // TODO: Coded error.
    throw new Error("Not enough accounts");
  }
  let accountIndex = 0;
  const getNextAccount = () => {
    const accountMeta = (instruction.accounts as TAccountMetas)[accountIndex]!;
    accountIndex += 1;
    return accountMeta;
  };
  return {
    programAddress: instruction.programAddress,
    accounts: {
      escrow: getNextAccount(),
      agent: getNextAccount(),
      agentOwner: getNextAccount(),
    },
    data: getSubmitDeliveryInstructionDataDecoder().decode(instruction.data),
  };
}
